import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Crown, MapPin, Quote, Award, Loader2, Send } from "lucide-react";
import { Header } from "@/components/Header";
import { LiyaPataDivider } from "@/components/Motifs";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Ambassadors() {
  const { lang } = useLang();
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ nominee_name: "", nominee_place: "", honour: "", reason: "", nominator_name: "", nominator_email: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/ambassadors`).then((r) => setList(r.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.nominee_name.trim().length < 2) { toast.error("Please enter the name of the person you wish to honour"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ambassador-nominations`, form);
      toast.success(data.message || "Nomination received 🌸");
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the nomination");
    } finally { setBusy(false); }
  };

  const L = (en, si, ta) => (lang === "si" ? si : lang === "ta" ? ta : en);
  const inputCls = "w-full h-13 px-4 py-3 rounded-2xl font-ui text-base outline-none";
  const inputStyle = { background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" };

  return (
    <div className="min-h-screen pb-24" style={{ background: "var(--bg)" }}>
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "var(--hero-gradient)" }} data-testid="amb-hero">
        <div className="absolute inset-0 sk-mesh pointer-events-none" />
        <LiyaPataDivider className="absolute top-5 left-1/2 -translate-x-1/2 w-56 opacity-30 pointer-events-none" style={{ color: "#FFD873" }} />
        <div className="relative max-w-4xl mx-auto px-6 pt-10 pb-14 sm:pt-14 sm:pb-20 text-center">
          <Link to="/rooms/sindu-kamare" data-testid="amb-back" className="inline-flex items-center gap-2 font-ui text-sm font-bold mb-8 text-white/85 hover:text-white">
            <ArrowLeft size={18} /> {L("Back to the room", "කාමරයට ආපසු", "அறைக்குத் திரும்பு")}
          </Link>
          <span className="grid place-items-center w-16 h-16 rounded-2xl mx-auto mb-5 sk-pulse-glow" style={{ background: "rgba(255,215,120,0.16)", color: "#FFD873", border: "1px solid rgba(255,215,120,0.4)" }}>
            <Crown size={32} />
          </span>
          <p className="font-ui text-xs font-bold tracking-[0.25em] uppercase mb-3 flex items-center justify-center gap-2" style={{ color: "rgba(255,216,115,0.85)" }}>
            <span className="sk-gem gold" /> {L("Patrons of Honour", "ගෞරවනීය අනුග්‍රාහකයෝ", "கௌரவ ஆதரவாளர்கள்")} <span className="sk-gem" />
          </p>
          <h1 className="sk-display text-4xl sm:text-5xl font-extrabold leading-tight" style={{ color: "#FFFFFF" }}>
            {L("Our Ambassadors", "අපගේ තානාපතිවරු", "எங்கள் தூதர்கள்")}
          </h1>
          <p className="font-ui text-lg mt-5 max-w-2xl mx-auto" style={{ color: "rgba(245,243,255,0.85)" }}>
            {L(
              "Distinguished elders across the world who lend their grace and good name to Neox — honoured with a certificate and a place of pride on our wall.",
              "ලොව පුරා සිටින ගෞරවනීය වැඩිහිටියන් — Neox වෙත ඔවුන්ගේ ගෞරවය හා නම පුද කරති. සහතිකයක් හා අපගේ බිත්තියේ ගෞරවනීය ස්ථානයක් ඔවුන්ට හිමිය.",
              "உலகெங்கிலும் உள்ள கண்ணியமான பெரியவர்கள் — Neox-க்கு தங்கள் கண்ணியத்தை வழங்குகிறார்கள். சான்றிதழும் எங்கள் சுவரில் பெருமை இடமும் வழங்கப்படுகிறது."
            )}
          </p>
          <p className="font-ui text-sm mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,216,115,0.9)", border: "1px solid rgba(255,215,120,0.3)" }} data-testid="amb-sponsor">
            <Award size={15} /> {L("Honorary certificate · proudly presented in partnership with our patron brands", "ගෞරව සහතිකය · අපගේ අනුග්‍රාහක සන්නාමයන් සමඟ", "கௌரவ சான்றிதழ் · எங்கள் ஆதரவு நிறுவனங்களுடன்")}
          </p>
        </div>
      </section>

      {/* Ambassador grid */}
      <section className="max-w-5xl mx-auto px-5 sm:px-6 py-12" data-testid="amb-grid">
        {list.length === 0 ? (
          <p className="text-center font-ui py-10" style={{ color: "var(--text-muted)" }}>The hall of honour is being prepared. 👑</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {list.map((a) => (
              <div key={a.id} data-testid={`amb-card-${a.id}`} className="rounded-3xl p-6 sk-luxe sk-gold-edge transition-transform hover:-translate-y-1"
                style={{ boxShadow: "0 16px 40px -22px var(--accent-glow)" }}>
                <div className="flex items-center gap-4">
                  <img src={a.photo_url} alt={a.name} loading="lazy" className="w-20 h-20 rounded-2xl object-cover shrink-0" style={{ border: "2px solid var(--gold-2)" }} />
                  <div className="min-w-0">
                    <h3 className="font-display text-xl font-extrabold leading-tight" style={{ color: "var(--text)" }}>{a.name}</h3>
                    <p className="font-ui text-sm font-bold mt-1 sk-gold-foil inline-block">{a.honour}</p>
                    {a.place && <p className="font-ui text-xs mt-1 flex items-center gap-1" style={{ color: "var(--text-muted)" }}><MapPin size={12} /> {a.place}</p>}
                  </div>
                </div>
                {a.quote && (
                  <blockquote className="mt-4 font-ui text-base italic leading-relaxed flex gap-2" style={{ color: "var(--text-2)" }}>
                    <Quote size={18} className="shrink-0 mt-1" style={{ color: "var(--gold-2)" }} />
                    <span>{a.quote}</span>
                  </blockquote>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Nomination */}
      <section className="max-w-2xl mx-auto px-6 pb-8" data-testid="amb-nominate">
        <LiyaPataDivider className="mb-8 mx-auto max-w-xs" style={{ color: "var(--gold)" }} />
        <div className="rounded-3xl p-7 sm:p-9" style={{ background: "var(--membership-card-bg)", border: "1px solid rgba(255,215,120,0.35)" }}>
          <h2 className="sk-display text-2xl sm:text-3xl font-extrabold" style={{ color: "#FFFFFF" }}>
            {L("Nominate a person of honour", "ගෞරවනීය පුද්ගලයෙකු නම් කරන්න", "கௌரவ நபரை பரிந்துரைக்கவும்")}
          </h2>
          <p className="font-ui text-base mt-3" style={{ color: "rgba(245,243,255,0.82)" }}>
            {L(
              "We do not ask people to nominate themselves. If you respect a distinguished elder in your community — here or abroad — propose their name, and we will reach out with the honour they deserve.",
              "කිසිවෙකුට තමන්වම නම් කිරීමට අපි නොකියමු. ඔබේ ප්‍රජාවේ — මෙහි හෝ විදේශයක — ගෞරවනීය වැඩිහිටියෙකුට ඔබ ගරු කරන්නේ නම්, ඔවුන්ගේ නම යෝජනා කරන්න.",
              "தங்களையே பரிந்துரைக்கச் சொல்ல மாட்டோம். உங்கள் சமூகத்தில் — இங்கே அல்லது வெளிநாட்டில் — ஒரு கண்ணியமான பெரியவரை நீங்கள் மதித்தால், அவர் பெயரை முன்மொழியுங்கள்."
            )}
          </p>

          {sent ? (
            <div className="mt-6 rounded-2xl p-5 text-center" style={{ background: "rgba(255,255,255,0.08)", color: "#FFE8B0" }} data-testid="amb-sent">
              🌸 {L("Thank you — your nomination has been received with gratitude.", "ස්තූතියි — ඔබගේ යෝජනාව ගෞරවයෙන් ලැබී ඇත.", "நன்றி — உங்கள் பரிந்துரை பெறப்பட்டது.")}
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-3">
              <input data-testid="amb-nominee-name" value={form.nominee_name} onChange={(e) => set("nominee_name", e.target.value)} placeholder={L("Name of the person you wish to honour", "ගෞරව කිරීමට අපේක්ෂිත පුද්ගලයාගේ නම", "கௌரவிக்க விரும்பும் நபரின் பெயர்")} className={inputCls} style={inputStyle} />
              <input data-testid="amb-nominee-place" value={form.nominee_place} onChange={(e) => set("nominee_place", e.target.value)} placeholder={L("City & country", "නගරය සහ රට", "நகரம் & நாடு")} className={inputCls} style={inputStyle} />
              <input data-testid="amb-honour" value={form.honour} onChange={(e) => set("honour", e.target.value)} placeholder={L("Their standing / honour (optional)", "ඔවුන්ගේ තත්ත්වය (අත්‍යවශ්‍ය නොවේ)", "அவர்களின் நிலை (விருப்பம்)")} className={inputCls} style={inputStyle} />
              <textarea data-testid="amb-reason" value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder={L("Why do they deserve this honour?", "ඔවුන් මෙම ගෞරවයට සුදුසු ඇයි?", "ஏன் இந்த கௌரவத்திற்கு தகுதியானவர்?")} rows={3} className={`${inputCls} h-auto`} style={inputStyle} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input data-testid="amb-nominator-name" value={form.nominator_name} onChange={(e) => set("nominator_name", e.target.value)} placeholder={L("Your name", "ඔබගේ නම", "உங்கள் பெயர்")} className={inputCls} style={inputStyle} />
                <input data-testid="amb-nominator-email" value={form.nominator_email} onChange={(e) => set("nominator_email", e.target.value)} placeholder={L("Your email", "ඔබගේ ඊමේල්", "உங்கள் மின்னஞ்சல்")} className={inputCls} style={inputStyle} />
              </div>
              <button type="submit" disabled={busy} data-testid="amb-submit" className="w-full h-13 py-3 rounded-full font-ui font-extrabold text-base flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "linear-gradient(100deg, var(--vermilion), var(--sigiriya-gold))", color: "#1a0a05" }}>
                {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} {L("Send nomination", "යෝජනාව යවන්න", "பரிந்துரையை அனுப்பு")}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
